import React, { useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { Loader2 } from "lucide-react";

import SettingRow from "@/components/shared/SettingRow";
import RetryButton from "@/components/shared/RetryButton";
import { Switch } from "@/components/ui/Switch";
import ModuleGate from "@/permissions/ModuleGate";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULE_STATE } from "@/permissions/modules";
import useLiveResource from "@/hooks/useLiveResource";
import { TenantSettingsService } from "@/services/tenantSettingsService";

/**
 * The job form's consent section, step 2. One switch per email-consent flow,
 * answered for this job rather than for the whole tenant.
 *
 * Structural sibling of ShiftSelector and CalendarTypeSelector — it takes the
 * parent's `formMethods` and writes straight onto the form, no onChange
 * callback. It renders `SettingRow` + `Switch`, the same pair the org-settings
 * page uses, so the two screens read as one control rather than two
 * implementations of a toggle.
 *
 * ## Nothing here is written in JSX
 *
 * Labels, helper text, the render order, which flow a module gates, the form
 * field name and the wire key all come off `emailConsentFlows` on
 * `GET /tenant-settings`. `settingKey` is the camelCase form field,
 * `column` the snake_case key jobService puts on the wire — both served, so
 * neither is restated here. `helper` is one plain string: the org page's old
 * `<strong>` clause is gone on purpose and must not be recovered.
 *
 * ## Override mode
 *
 * A flow the org enforces (`enforceSettingKey` on the same response) is not
 * this job's to answer. Its row still renders — the recruiter has to be able to
 * see what applies — showing the org value, disabled, with a note saying where
 * it comes from. It writes no form field at all, so the payload drops the key;
 * the server drops it too, and this only keeps the payload honest rather than
 * leaning on the server alone.
 *
 * ## One pre-set rule for create, clone and edit
 *
 * A field still `undefined` gets the org default. A field that already holds a
 * value is left alone. That is all three cases:
 *
 *   create / clone — nothing on the form, so the org default at the moment the
 *                    form opened (decision 4: a clone starts from the org
 *                    default, not from the source job).
 *   edit           — EditJobForm has already put the job's own resolved value
 *                    on the form, and it wins over the org default even when
 *                    the job is only following it.
 *
 * A flow whose module the tenant does not own is neither drawn nor pre-set,
 * and a value Edit seeded it with is cleared — see GatedConsentRow. Its field
 * ends `undefined` either way, so `buildEmailConsentPayload` drops the key and
 * whatever the job already stored survives the module being withheld.
 *
 * ## It owns its own three screens
 *
 * Its own spinner and its own retry, never shared with FieldConfigSelector —
 * that fetches separately and can succeed while this fails, and a shared error
 * screen would ask the user to retry a call that worked. `onReadyChange` is
 * what blocks step 2's submit until the defaults are on screen: false while
 * loading and after a failure, true once the rows are drawn.
 *
 * @param {object}  formMethods    the parent's useForm return
 * @param {object}  [inheritedFlags] the job's three `*_inherited` flags, keyed
 *                                 by `${column}_inherited`. Absent for a new job.
 * @param {boolean} [disabled]
 * @param {(ready: boolean) => void} [onReadyChange]
 */
const ConsentConfigSelector = ({ formMethods, inheritedFlags, disabled = false, onReadyChange }) => {
  const [flows, setFlows] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [enforced, setEnforced] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await TenantSettingsService.getSettings();
      const served = response?.data?.emailConsentFlows ?? [];

      // The org default per flow, read off the same response by the flow's own
      // `settingKey`. Handed to the row, which is where the pre-set happens —
      // see the note on ConsentRow.
      setDefaults(Object.fromEntries(served.map((flow) => [flow.settingKey, Boolean(response?.data?.[flow.settingKey])])));
      // The mode, off the same response by the flow's own `enforceSettingKey`.
      // Whether the row can be answered here at all — see ConsentRow.
      setEnforced(Object.fromEntries(served.map((flow) => [flow.settingKey, Boolean(response?.data?.[flow.enforceSettingKey])])));
      setFlows(served);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load consent settings");
      setFlows(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The org defaults and the Override mode above are edited on Organization
  // Settings, and this read them once on mount — so a recruiter with the job
  // form open kept offering a switch the org had just frozen, and saw the old
  // default on every new job until they reloaded (G1).
  // `tenant_config.updated` is unfiltered; the subscription is sent anyway so
  // every call site in the app reads the same way.
  useLiveResource({
    keys: "tenant_config",
    onChange: load,
  });

  useEffect(() => {
    onReadyChange?.(Boolean(flows) && !loading && !error);
  }, [flows, loading, error, onReadyChange]);

  if (loading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="Loading consent settings">
        <Loader2 className="h-8 w-8 animate-spin text-text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="mb-4 text-tone-rose">{error}</p>
        <RetryButton onClick={load} className="px-4 py-2 text-sm" />
      </div>
    );
  }

  if (!flows?.length) return null;

  return (
    <div data-field="consentConfigSelector" className="flex flex-col gap-3">
      <div>
        <h3 className="mb-1 text-[16px] font-semibold text-text-primary">Consent Confirmation Emails</h3>
        <p className="text-sm text-text-muted">
          Require the candidate to confirm by email before the action below goes through. Each toggle starts from your organization&apos;s
          setting; saving this job locks in your own answer for it.
        </p>
      </div>

      {flows.map((flow) => {
        const props = {
          flow,
          orgDefault: defaults[flow.settingKey],
          formMethods,
          inherited: Boolean(inheritedFlags?.[`${flow.column}_inherited`]),
          enforced: Boolean(enforced[flow.settingKey]),
          disabled,
        };

        return flow.module ? <GatedConsentRow key={flow.settingKey} {...props} /> : <ConsentRow key={flow.settingKey} {...props} />;
      })}
    </div>
  );
};

/**
 * A flow the tenant may not own, and the only place its field is ever cleared.
 *
 * A row about a flow the tenant does not own explains nothing and invites a
 * support ticket, so the not-entitled branch draws nothing — but drawing
 * nothing is not enough on Edit. EditJobForm seeds all three fields from the
 * job's own resolved booleans before this section mounts, and the route always
 * sends a boolean for every flow. With no row to clear it, the withheld flow's
 * seeded value rode `buildEmailConsentPayload` onto the wire, where the
 * server's `_assert_selection_entitled` reads any present key as a selection
 * and refuses it — so every job edit 403'd for every tenant without the
 * module, whatever the recruiter had changed. Clearing here is what keeps the
 * key out of the payload, and with it out, the job's stored value survives the
 * module being withheld.
 *
 * `ModuleGate` still wraps the row it does draw: the unavailable branch is a
 * kill switch, not an entitlement, and its presentation is the gate's.
 * `w-full`: the row spans the section, so the wrapper has to as well.
 */
const GatedConsentRow = (props) => {
  const { flow, formMethods } = props;
  const { getValues, setValue } = formMethods;
  const withheld = useModuleAccess(flow.module).state === MODULE_STATE.NOT_ENTITLED;

  useEffect(() => {
    if (withheld && getValues(flow.settingKey) !== undefined) {
      setValue(flow.settingKey, undefined);
    }
  }, [withheld, flow.settingKey, getValues, setValue]);

  if (withheld) return null;

  return (
    <ModuleGate module={flow.module} className="w-full">
      <ConsentRow {...props} />
    </ModuleGate>
  );
};

/**
 * One flow's row, and the only place its form field is ever written.
 *
 * The pre-set lives here rather than in the fetch on purpose. A withheld flow
 * draws no row, so this component never mounts and this effect never runs —
 * which is how its field is never pre-set. (Clearing a field the job form was
 * opened with is GatedConsentRow's job, above.) Pre-setting in the parent's
 * `load()` wrote `false` for a row the tenant was never shown, and that would
 * have overwritten a value the job had already stored.
 *
 * An enforced row clears its field instead of pre-setting it, for the same
 * reason and with the same effect: the key leaves the payload, and the job's
 * own stored answer survives the org's Override so it is there again when the
 * org returns to Default.
 */
const ConsentRow = ({ flow, orgDefault, formMethods, inherited, enforced, disabled }) => {
  const { control, getValues, setValue } = formMethods;

  useEffect(() => {
    if (enforced) {
      if (getValues(flow.settingKey) !== undefined) {
        setValue(flow.settingKey, undefined);
      }
      return;
    }
    if (getValues(flow.settingKey) === undefined) {
      setValue(flow.settingKey, Boolean(orgDefault));
    }
  }, [flow.settingKey, orgDefault, enforced, getValues, setValue]);

  // Subscribed to this one field, not to the form. A bare `watch()` on the
  // parent's methods re-rendered the whole section on every keystroke in step 2.
  const watched = Boolean(useWatch({ control, name: flow.settingKey }));
  // Under Override the org value is what applies, and it is not on the form.
  const checked = enforced ? Boolean(orgDefault) : watched;

  // Written here rather than served from the registry: it is one string for all
  // three flows, and it says something the org page's own note does not — so it
  // is not one truth with two homes. Leave it here.
  const note = enforced ? (
    <p className="text-[13px] leading-relaxed text-text-secondary">
      Set by your organization and applied to every job. It cannot be changed here.
    </p>
  ) : inherited ? (
    <p className="text-[13px] leading-relaxed text-tone-amber">
      Currently following your organization&apos;s setting. Saving this job locks in the value shown here.
    </p>
  ) : null;

  return (
    <SettingRow
      title={flow.label}
      description={flow.helper}
      note={note}
      control={
        <Switch
          checked={checked}
          disabled={disabled || enforced}
          onToggle={() => setValue(flow.settingKey, !getValues(flow.settingKey), { shouldDirty: true })}
          label={flow.label}
        />
      }
    />
  );
};

export default ConsentConfigSelector;
